import { OPERATOR_GT_EQUALS, OPERATOR_LT_EQUALS } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { JqlData } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import type { DateTimeValue, TypeConfig } from '../../common/types.tsx';
import messages from './messages.tsx';
import {
	getTimeInfo,
	getTimeInMinutes,
	getTimeString,
	IS_YYYY_MM_DD_REGEX,
} from './time-functions.tsx';

export const IN_RANGE_CONFIG: TypeConfig = {
	type: 'in_range',
	componentType: 'in_range',
	label: 'labelInRange',
	hasInput: true,
	getErrorMessage: (intl: IntlShape, value?: DateTimeValue): string | undefined => {
		if (
			value?.value1 &&
			value.value2 &&
			getTimeInMinutes(getTimeInfo(value.value1)) > getTimeInMinutes(getTimeInfo(value.value2))
		)
			return intl.formatMessage(messages.validationMessageFromGreaterThanTo);
		if (
			value &&
			((value.value1 !== '' && !getTimeInfo(value.value1).isValid) ||
				(value.value2 !== '' && !getTimeInfo(value.value2).isValid))
		)
			return intl.formatMessage(messages.validationMessageInvalidPeriodFormatValue);
		return undefined;
	},
	getTooltip: (intl: IntlShape, value?: DateTimeValue): string | undefined => {
		const fromTimeInfo = getTimeInfo(value?.value1);
		const toTimeInfo = getTimeInfo(value?.value2);

		if (fromTimeInfo.isValid && toTimeInfo.isValid) {
			if (fromTimeInfo.isBeforeNow && toTimeInfo.isBeforeNow) {
				return intl.formatMessage(messages.valueInRangeBeforeNowToBeforeNow, {
					beforeTime: getTimeString(intl, fromTimeInfo),
					afterTime: getTimeString(intl, toTimeInfo),
				});
			}

			if (fromTimeInfo.isBeforeNow && !toTimeInfo.isBeforeNow) {
				return intl.formatMessage(messages.valueInRangeBeforeNowToAfterNow, {
					beforeTime: getTimeString(intl, fromTimeInfo),
					afterTime: getTimeString(intl, toTimeInfo),
				});
			}

			if (!fromTimeInfo.isBeforeNow && !toTimeInfo.isBeforeNow) {
				return intl.formatMessage(messages.valueInRangeAfterNowToAfterNow, {
					beforeTime: getTimeString(intl, fromTimeInfo),
					afterTime: getTimeString(intl, toTimeInfo),
				});
			}
		}

		return undefined;
	},
	getFormattedValue: (intl: IntlShape, value?: DateTimeValue): string => {
		const fromTimeInfo = getTimeInfo(value?.value1);
		const toTimeInfo = getTimeInfo(value?.value2);

		if (fromTimeInfo.isValid && toTimeInfo.isValid) {
			return intl.formatMessage(messages.valueInRange, {
				start: value?.value1,
				end: value?.value2,
			});
		}

		if (fromTimeInfo.isValid && value && value.value2 === '') {
			return fromTimeInfo.isBeforeNow
				? intl.formatMessage(messages.valueInRangeBeforeNowToBlank, {
						beforeTime: getTimeString(intl, fromTimeInfo),
					})
				: intl.formatMessage(messages.valueInRangeAfterNowToBlank, {
						beforeTime: getTimeString(intl, fromTimeInfo),
					});
		}

		if (toTimeInfo.isValid && value && value.value1 === '') {
			return toTimeInfo.isBeforeNow
				? intl.formatMessage(messages.valueInRangeBlankToBeforeNow, {
						afterTime: getTimeString(intl, toTimeInfo),
					})
				: intl.formatMessage(messages.valueInRangeBlankToAfterNow, {
						afterTime: getTimeString(intl, toTimeInfo),
					});
		}

		// TODO : This scenario shouldn't happen, retain this till invalid scenarios are handled
		return value
			? intl.formatMessage(messages.valueInRangeBeforeNowToAfterNow, {
					beforeTime: value.value1,
					afterTime: value.value2,
				})
			: '';
	},
	getJqlData: (dateTimeValue?: DateTimeValue) => {
		if (!dateTimeValue) {
			return undefined;
		}
		const { value1, value2 } = dateTimeValue;

		return {
			primaryClause: value1
				? {
						operator: OPERATOR_GT_EQUALS,
						operand: `${value1}`,
					}
				: undefined,
			secondaryClause: value2
				? {
						operator: OPERATOR_LT_EQUALS,
						operand: `${value2}`,
					}
				: undefined,
		};
	},
	getValueFromJqlData: (jqlData?: JqlData): DateTimeValue | undefined => {
		if (jqlData !== undefined) {
			const { primaryClause, secondaryClause } = jqlData;
			if (primaryClause === undefined && secondaryClause === undefined) {
				// Do not match type if both clauses are not present
				return undefined;
			}
			if (primaryClause !== undefined || secondaryClause !== undefined) {
				const returnValue = { value1: '', value2: '' };
				[primaryClause, secondaryClause].forEach((clause) => {
					const operatorValue = clause && clause.operator ? clause.operator : '';
					const value = clause && clause.operand !== undefined ? clause.operand : '';
					switch (operatorValue) {
						case OPERATOR_GT_EQUALS: {
							if (value.match(IS_YYYY_MM_DD_REGEX) == null) {
								returnValue.value1 = value;
							}
							break;
						}
						case OPERATOR_LT_EQUALS: {
							if (value.match(IS_YYYY_MM_DD_REGEX) == null) {
								returnValue.value2 = value;
							}
							break;
						}

						default:
							break;
					}
				});
				if (returnValue.value1 || returnValue.value2) {
					return returnValue;
				}
			}
			return undefined;
		}
		return undefined;
	},
};
